import type { SankeyLink as D3SankeyLink } from 'd3-sankey';

import type { SankeyLink, SankeyNode, SankeyNodeValidated } from './types';

const SQRT_MIN_LAYOUT_VALUE = 1;

export type SankeyValueScale = 'linear' | 'sqrt';

/** Sqrt-compress layout magnitudes (with a floor) so small flows stay legible. */
export const scaleLayoutValue = (
  rawValue: number,
  scale: SankeyValueScale
): number => {
  if (scale !== 'sqrt') return rawValue;
  if (rawValue <= 0) return 0;
  return Math.max(Math.sqrt(rawValue), SQRT_MIN_LAYOUT_VALUE);
};

const nodeCenterY = (node: SankeyNodeValidated): number =>
  (node.y0 + node.y1) / 2;

/** Per-link attachment Y at each end, keyed by link index. */
export type SankeyLinkEndpoints = {
  sourceY: Map<number, number>;
  targetY: Map<number, number>;
};

export const EMPTY_LINK_ENDPOINTS: SankeyLinkEndpoints = {
  sourceY: new Map(),
  targetY: new Map(),
};

/** Re-space a column to fill the plot height (lone node centered). */
const distributeColumn = (
  columnNodes: SankeyNodeValidated[],
  top: number,
  plotHeight: number
): void => {
  if (columnNodes.length === 0) return;
  const ordered = [...columnNodes].sort((a, b) => a.y0 - b.y0);
  const heights = ordered.map((node) => node.y1 - node.y0);
  const totalHeight = heights.reduce((sum, h) => sum + h, 0);
  const slack = Math.max(0, plotHeight - totalHeight);
  const gap = ordered.length > 1 ? slack / (ordered.length - 1) : 0;

  let cursor = ordered.length === 1 ? top + slack / 2 : top;
  ordered.forEach((node, i) => {
    const height = heights[i] ?? 0;
    node.y0 = cursor;
    node.y1 = cursor + height;
    cursor += height + gap;
  });
};

/** Spread one side's ribbons across the node bar, ordered by partner Y. */
const distributeSide = (
  node: SankeyNodeValidated,
  links: D3SankeyLink<SankeyNode, SankeyLink>[],
  partner: (link: D3SankeyLink<SankeyNode, SankeyLink>) => SankeyNodeValidated,
  out: Map<number, number>,
  center = false
): void => {
  if (links.length === 0) return;
  const ordered = [...links].sort(
    (a, b) => nodeCenterY(partner(a)) - nodeCenterY(partner(b))
  );
  const totalWidth = ordered.reduce((sum, link) => sum + (link.width ?? 0), 0);
  const nodeHeight = node.y1 - node.y0;
  const slack = Math.max(0, nodeHeight - totalWidth);
  const packed = center || ordered.length === 1;
  const gap = packed || ordered.length <= 1 ? 0 : slack / (ordered.length - 1);

  let cursor = packed ? node.y0 + slack / 2 : node.y0;
  ordered.forEach((link) => {
    const width = link.width ?? 0;
    if (link.index !== undefined) {
      out.set(link.index, cursor + width / 2);
    }
    cursor += width + gap;
  });
};

/** Fill every column to the plot height and resolve each ribbon's attachment Y. */
export const applyColumnFillLayout = ({
  nodes,
  top,
  plotHeight,
}: {
  nodes: SankeyNodeValidated[];
  top: number;
  plotHeight: number;
}): SankeyLinkEndpoints => {
  const byDepth = new Map<number, SankeyNodeValidated[]>();
  for (const node of nodes) {
    const column = byDepth.get(node.depth);
    if (column) {
      column.push(node);
    } else {
      byDepth.set(node.depth, [node]);
    }
  }
  byDepth.forEach((column) => {
    distributeColumn(column, top, plotHeight);
  });

  // Endpoints come from the final node positions — no link dragging needed.
  const sourceY = new Map<number, number>();
  const targetY = new Map<number, number>();
  for (const node of nodes) {
    const intermediate =
      (node.sourceLinks?.length ?? 0) > 0 &&
      (node.targetLinks?.length ?? 0) > 0;
    distributeSide(
      node,
      node.sourceLinks ?? [],
      (link) => link.target as SankeyNodeValidated,
      sourceY,
      intermediate
    );
    distributeSide(
      node,
      node.targetLinks ?? [],
      (link) => link.source as SankeyNodeValidated,
      targetY,
      intermediate
    );
  }
  return { sourceY, targetY };
};
